import React from 'react';
import { motion } from 'framer-motion';

const CTA: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8 },
    },
  };

  return (
    <section className="relative overflow-hidden bg-slate-50 py-24 sm:py-32 lg:py-40" id="cta">
      {/* Ambient background optimized for light mode */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 right-1/3 w-[600px] h-[600px] rounded-full blur-[140px] opacity-[0.06] bg-gradient-to-l from-cyan-400/30 to-transparent" />
        <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] rounded-full blur-[130px] opacity-[0.04] bg-gradient-to-t from-indigo-400/20 to-transparent" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 sm:px-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="space-y-12"
        >
          {/* Main CTA card */}
          <motion.div
            variants={itemVariants}
            className="rounded-[2.5rem] border border-slate-100 bg-white p-12 lg:p-20 space-y-12 shadow-premium"
          >
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2.5 px-3.5 py-2 rounded-lg bg-slate-50 border border-slate-200 shadow-sm">
                <div className="w-1.5 h-1.5 rounded-full bg-cyan-600 shadow-[0_0_8px_rgba(8,145,178,0.3)]" />
                <span className="text-xs font-bold uppercase tracking-[0.3em] text-slate-500">
                  Únete ahora
                </span>
              </div>

              <div className="space-y-6 max-w-3xl">
                <h2 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.12] tracking-tight text-slate-900">
                  Construye el futuro con{' '}
                  <span className="text-cyan-600">
                    IEEE ESPOCH
                  </span>
                </h2>
                <p className="text-lg sm:text-xl text-slate-600 leading-relaxed font-normal max-w-2xl">
                  Únete a una comunidad universitaria premium que impulsa oportunidades, proyectos de alto valor y liderazgo tecnológico con impacto global.
                </p>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <motion.a
                  href="#student-branch"
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center justify-center px-10 py-4 rounded-xl font-bold text-sm uppercase tracking-[0.2em] bg-cyan-600 text-white transition-all duration-300 shadow-premium hover:bg-cyan-500"
                >
                  Únete a IEEE ESPOCH
                </motion.a>
                <motion.a
                  href="#benefits"
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center justify-center px-10 py-4 rounded-xl font-bold text-sm uppercase tracking-[0.2em] text-slate-900 transition-all duration-300 bg-white border border-slate-200 hover:bg-slate-50 hover:border-slate-300 shadow-sm"
                >
                  Conoce los beneficios
                </motion.a>
              </div>
            </div>
          </motion.div>

          {/* Stats Grid */}
          <motion.div
            variants={containerVariants}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8"
          >
            {[
              { value: '160+', label: 'Miembros activos en ESPOCH', description: 'Estudiantes de ingeniería comprometidos' },
              { value: '9', label: 'Capítulos técnicos', description: 'Ramas especializadas de alto nivel técnico' },
              { value: '12+', label: 'Eventos anuales', description: 'Conferencias, hackathons y workshops' },
            ].map((stat, idx) => (
              <motion.div
                key={idx}
                variants={itemVariants}
                className="group relative rounded-3xl p-8 border border-slate-100 bg-white transition-all duration-500 hover:border-cyan-200 hover:shadow-premium"
              >
                <div className="relative space-y-4">
                  <div className="text-4xl sm:text-5xl font-black text-slate-900 leading-tight">
                    {stat.value}
                  </div>
                  <div className="space-y-2">
                    <div className="text-base font-bold text-slate-800">
                      {stat.label}
                    </div>
                    <p className="text-sm text-slate-500 font-normal">
                      {stat.description}
                    </p>
                  </div>
                </div>

                <div className="absolute bottom-0 left-8 right-8 h-1 bg-gradient-to-r from-cyan-500/40 to-transparent w-0 group-hover:w-[calc(100%-64px)] transition-all duration-500 rounded-full" />
              </motion.div>
            ))}
          </motion.div>

          {/* Trust statement */}
          <motion.div
            variants={itemVariants}
            className="text-center space-y-8 max-w-2xl mx-auto"
          >
            <p className="text-lg text-slate-600 leading-relaxed font-normal">
              IEEE es la organización de profesionales más grande del mundo con 420K+ miembros en 160 países. ESPOCH es el punto de entrada a esta red global de innovación, liderazgo y oportunidades técnicas.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-500">
              <span className="flex items-center gap-2 font-semibold">
                <svg className="w-5 h-5 text-cyan-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Acceso global
              </span>
              <span className="flex items-center gap-2 font-semibold">
                <svg className="w-5 h-5 text-cyan-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Crecimiento profesional
              </span>
              <span className="flex items-center gap-2 font-semibold">
                <svg className="w-5 h-5 text-cyan-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Impacto real
              </span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default CTA;
